/**
 * Search Autocomplete UI Layer*/

class SearchAutoCompleteUILayer {
  constructor () {
    this.eventBus = window.theme.tagalys.searchAutocomplete.eventBus
    this.container = document.getElementById('Search-Drawer')

    if (!this.container || !this.eventBus) return

    this.buttons = document.querySelectorAll('.thb-quick-search')
    this.form = this.container.querySelector('form.searchform')
    this.input = this.container.querySelector('input[type="search"]')
    this.results = this.container.querySelector('.side-panel-content--has-tabs')
    this.config = (window.theme.resultsConfig && window.theme.resultsConfig.card) || {}
    this.translations = window.theme.tagalys.searchAutocomplete.translations || {}
    this.liveRegion = this.ensureLiveRegion()

    this.bindOpen()
    this.bindInput()
    this.subscribeToState()
  }

  /* ---------- Drawer open (close handled globally by <side-panel-close>) ---------- */

  bindOpen () {
    this.buttons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault()

        document.documentElement.classList.add('overflow-hidden');

        document.body.classList.toggle('open-cc')
        this.container.classList.toggle('active')

        if (this.container.classList.contains('active')) {
          setTimeout(() => this.input && this.input.focus({ preventScroll: true }), 100)
          dispatchCustomEvent('search:open')
        }

        return false
      })
    })
  }

  /* ---------- Search input ---------- */

  bindInput () {
    if (!this.input || !this.form) return

    this.input.addEventListener('input', debounce(() => this.onInput(), 200))

    this.form.addEventListener('submit', (event) => {
      const term = this.getQuery()

      if (!term.length) {
        event.preventDefault()

        return
      }

      this.eventBus.publish('searchAutoComplete:state:term:submitted', term)
    })
  }

  getQuery () {
    return this.input ? this.input.value.trim() : ''
  }

  onInput () {
    const term = this.getQuery()

    if (!this.results) return

    if (!term.length) {
      this.results.classList.remove('active', 'loading')
      this.results.innerHTML = ''

      return
    }

    this.results.classList.add('active', 'loading')
    this.eventBus.publish('searchAutoComplete:ui:term:updated', { term })
  }

  /* ---------- Reactive render ---------- */

  subscribeToState () {
    if (!this.results) return

    this.eventBus.subscribe('searchAutoComplete:state:updated', (state) => this.render(state))
  }

  render (state) {
    if (!this.results) return

    this.results.classList.remove('loading')

    const products = ((state && state.products) || []).filter(product => product.available !== false)
    const term = (state && state.term) || this.getQuery()

    this.results.innerHTML = this.renderResults(products)
    this.announce(products.length, term)
    this.bindFooter()
  }

  renderResults (products) {
    const cards = products.map(product => this.renderCard(product)).join('')
    const viewAll = this.escape(this.translations.viewAll || 'View all results')

    const panel = cards
      ? `<div class="side-panel-content--inner">
          <div class="side-panel-content--tab-panel tab-active">
            ${cards}
          </div>
        </div>`
      : `<div class="side-panel-content--inner">
          <div class="no-results">
            <p class="fz-14_140">
              ${this.escape(this.translations.noResults || 'No results')}
            </p>
          </div>
        </div>
      `

    const footer = `
      <div class="side-panel-content--search-footer">
          <button class="button" id="search-results-submit"><span>${viewAll}</span></button>
      </div>`

    return panel + footer
  }

  /* ---------- Autocomplete card (mirrors snippets/product-card-small.liquid) ---------- */

  renderCard (product) {
    const url = this.productUrl(product)
    const title = this.escape(product.title)
    const image = product.featured_image || (product.images && product.images[0])

    return `
      <product-card class="product-cart-item">
        <div class="product-cart-item-image">
          <a class="product-cart-item-link" href="${url}" title="${title}">
            ${this.renderImage(image, product.title)}
          </a>
        </div>

        <div>
          <a href="${url}" title="${title}" class="product-cart-item__title fz-14_100">
            ${this.highlight(product.title)}
          </a>

          ${this.renderColor(product)}

          ${this.renderPrice(product)}
        </div>
      </product-card>
    `
  }

  renderImage (image, title) {
    if (!image || !image.src) {
      return '<div class="thb-placeholder" style="height:75px;"></div>'
    }

    const alt = this.escape(image.alt || title || '')
    const w = image.width || ''
    const h = image.height || ''

    return `<img class="product-primary-image" width="${w}" height="${h}" src="${this.cdn(image.src, 192)}" srcset="${this.cdn(image.src, 192)} 1x, ${this.cdn(image.src, 384)} 2x" alt="${alt}" loading="lazy">`
  }

  renderPrice (product) {
    const onSale = product.compare_at_price != null && product.compare_at_price > product.price
    const compare = onSale
      ? `<del><span class="amount">${this.money(product.compare_at_price)}</span></del>`
      : ''
    const priceText = product.price_varies
      ? `From ${this.money(product.price_min)}`
      : this.money(product.price)

    return `<span class="price">${compare}<ins><span class="amount">${priceText}</span></ins></span>`
  }

  renderColor (product) {
    const colourOption = product.options_with_values.find(option => {
      const name = option.name.toLowerCase()
      return name === 'color' || name === 'colour'
    })

    if (!colourOption || !colourOption.values || colourOption.values.length === 0) return ''

    return `
      <div class="product-cart-item__color fz-14_100">
        ${this.escape(colourOption.values[0])}
      </div>
    `
  }

  /* ---------- Footer + a11y ---------- */

  bindFooter () {
    const submit = this.results.querySelector('#search-results-submit')

    if (submit && this.form) {
      submit.addEventListener('click', () => this.form.submit())
    }
  }

  ensureLiveRegion () {
    let el = this.container.querySelector('.search-autocomplete-live')

    if (!el) {
      el = document.createElement('span')
      el.className = 'search-autocomplete-live visually-hidden'
      el.setAttribute('aria-live', 'polite')
      el.setAttribute('role', 'status')
      this.container.appendChild(el)
    }

    return el
  }

  announce (count, term) {
    if (!this.liveRegion) return

    this.liveRegion.textContent = count
      ? `${count} ${this.translations.ariaLiveHappy || 'results found for'} "${term}"`
      : `${this.translations.ariaLiveSad || 'No search results for'} "${term}"`
  }

  /* ---------- Helpers ---------- */

  money (amount) {
    const cents = Math.round((amount || 0) * 100)
    const fmt = this.config.moneyFormat || '${{amount}}'
    if (typeof formatMoney === 'function') return formatMoney(cents, fmt)
    return String(cents)
  }

  cdn (src, width) {
    if (!src) return ''

    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`
  }

  productUrl (product) {
    const root = (window.theme && window.theme.routes && window.theme.routes.root_url || '/').replace(/\/$/, '')

    return `${root}/products/${product.handle}`
  }

  highlight (title) {
    const safe = this.escape(title)
    const term = this.getQuery()

    if (!term) return safe

    const escTerm = this.escape(term).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    if (!escTerm) return safe

    return safe.replace(new RegExp(`(${escTerm})`, 'gi'), '<strong class="highlight">$1</strong>')
  }

  escape (str) {
    if (str == null) return ''

    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }
}

function initSearchAutoCompleteUI () {
    window.theme.tagalys.searchAutocomplete.uiLayer = new SearchAutoCompleteUILayer()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchAutoCompleteUI)
} else {
  initSearchAutoCompleteUI()
}
